import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Link, TextField, Typography } from '@mui/material';
import { AppHelper } from '../AppHelper';
import { isValidEmail } from '../helpers/email-validate';

const CODE_LENGTH = 6;
const INVALID_EMAIL_MSG = 'Please enter a valid email address, for example name@school.edu.';

// Off-screen field that real users never see or fill in; naive bots fill every input. The API ignores such requests.
const HONEYPOT_STYLE = { position: 'absolute', left: '-10000px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' };

// Email one-time-code sign-in. Step 1: email -> API sends a code. Step 2: code -> API returns a login ticket,
// which the parent exchanges for a session via api/profile/login (onVerified).
export default function LoginOtp({ onVerified }) {
  const [step, setStep] = useState('email'); // 'email' | 'code'
  const [email, setEmail] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);
  const [trap, setTrap] = useState(''); // honeypot
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [resendIn, setResendIn] = useState(0);
  const verifying = useRef(false);

  const trimmed = email.trim();
  const emailValid = isValidEmail(trimmed);
  const showEmailError = (emailTouched || error === INVALID_EMAIL_MSG) && trimmed !== '' && !emailValid;

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const t = setTimeout(() => setResendIn(resendIn - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const sendCode = async (e) => {
    if (e) e.preventDefault();
    if (busy) return;
    if (!isValidEmail(trimmed)) { setEmailTouched(true); setError(INVALID_EMAIL_MSG); return; }
    setBusy(true); setError(''); setInfo('');
    const res = await AppHelper.apiPost('api/auth/otp/request', { email: trimmed, website: trap });
    setBusy(false);
    if (!res) { setError('Could not reach the server. Please try again.'); return; }
    if (!res.status) {
      if (res.retryAfterSec) setResendIn(res.retryAfterSec);
      // A cooldown means a code was just sent: stay on / move to the code step.
      if (res.code === 'COOLDOWN') { setStep('code'); setInfo(res.message); return; }
      setError(res.message || 'Could not send the code. Please try again.');
      return;
    }
    setStep('code');
    setCode('');
    setResendIn(res.resendAfterSec || 30);
    setInfo('We emailed a ' + CODE_LENGTH + '-digit code to ' + trimmed + '. It may take a minute to arrive; check your spam folder too.');
  };

  const verify = async (value) => {
    if (verifying.current) return;
    verifying.current = true;
    setBusy(true); setError('');
    const res = await AppHelper.apiPost('api/auth/otp/verify', { email: trimmed, code: value });
    setBusy(false);
    verifying.current = false;
    if (!res || !res.status) {
      setError((res && res.message) || 'Could not verify the code. Please try again.');
      setCode('');
      return;
    }
    onVerified(res.email, res.loginTicket);
  };

  const onCodeChange = (e) => {
    const v = e.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH);
    setCode(v);
    if (v.length === CODE_LENGTH) verify(v);
  };

  const buttonSx = { backgroundColor: '#638889', borderRadius: '24px', textTransform: 'none', px: 4, '&:hover': { backgroundColor: '#527273' } };

  if (step === 'email') {
    return (
      <Box component="form" noValidate onSubmit={sendCode} data-testid="login-otp-email" sx={{ mt: 3, width: '100%', maxWidth: 360, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          type="email" label="Email address" value={email} autoFocus fullWidth
          autoComplete="email" inputProps={{ 'data-testid': 'login-email', inputMode: 'email', maxLength: 254 }}
          onChange={(e) => { setEmail(e.target.value); if (error === INVALID_EMAIL_MSG) setError(''); }}
          onBlur={() => setEmailTouched(true)} disabled={busy}
          error={showEmailError} helperText={showEmailError ? INVALID_EMAIL_MSG : ' '}
        />
        <input
          type="text" name="website" value={trap} onChange={(e) => setTrap(e.target.value)}
          tabIndex={-1} autoComplete="off" aria-hidden="true" data-testid="login-website" style={HONEYPOT_STYLE}
        />
        {error && error !== INVALID_EMAIL_MSG && <Typography color="error" variant="body2">{error}</Typography>}
        <Button type="submit" variant="contained" disabled={busy || !emailValid} sx={buttonSx} data-testid="login-button">
          {busy ? 'Sending...' : 'Email me a code'}
        </Button>
        <Typography variant="caption" color="text.secondary" textAlign="center">
          Use the same email you signed in with before and you will see your existing essays.
        </Typography>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={(e) => { e.preventDefault(); if (code.length === CODE_LENGTH) verify(code); }} data-testid="login-otp-code"
      sx={{ mt: 3, width: '100%', maxWidth: 360, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {info && <Typography variant="body2" color="text.secondary" textAlign="center">{info}</Typography>}
      <TextField
        label="6-digit code" value={code} autoFocus fullWidth onChange={onCodeChange} disabled={busy}
        autoComplete="one-time-code"
        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: CODE_LENGTH, 'data-testid': 'login-code', style: { letterSpacing: '0.5em', textAlign: 'center', fontSize: '1.4rem' } }}
      />
      {error && <Typography color="error" variant="body2">{error}</Typography>}
      <Button type="submit" variant="contained" disabled={busy || code.length !== CODE_LENGTH} sx={buttonSx}>
        {busy ? 'Checking...' : 'Sign in'}
      </Button>
      <Box display="flex" justifyContent="space-between">
        <Link component="button" type="button" underline="hover" onClick={() => { setStep('email'); setError(''); setInfo(''); setCode(''); }}>
          Use a different email
        </Link>
        <Link component="button" type="button" underline="hover" disabled={resendIn > 0 || busy} onClick={sendCode}
          sx={{ color: resendIn > 0 ? 'text.disabled' : undefined }}>
          {resendIn > 0 ? 'Resend code in ' + resendIn + 's' : 'Resend code'}
        </Link>
      </Box>
    </Box>
  );
}
